import React, { useState, useEffect } from 'react';
import api from '../utils/api';

function Bestandswarnung() {
  const [lowStockProducts, setLowStockProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [showStockModal, setShowStockModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [stockInput, setStockInput] = useState('');
  const [noteInput, setNoteInput] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadLowStock();
  }, []);

  const loadLowStock = async () => {
    try {
      setLoading(true);
      const data = await api.get('/api/products/low-stock');
      setLowStockProducts(data);
    } catch (err) {
      setMessage({ type: 'error', text: 'Fehler beim Laden: ' + err.message });
    } finally {
      setLoading(false);
    }
  };

  const openStockModal = (product) => {
    setSelectedProduct(product);
    setStockInput('');
    setNoteInput('');
    setShowStockModal(true);
  };

  const handleStockAdjustment = async () => {
    if (!stockInput || parseInt(stockInput) <= 0) {
      setMessage({ type: 'error', text: 'Bitte eine gültige Menge eingeben' });
      return;
    }

    if (processing) return; // Prevent double-click

    try {
      setProcessing(true);
      await api.post('/api/products/stock-adjustment', {
        product_id: selectedProduct.product_id,
        quantity: parseInt(stockInput),
        reason: noteInput || 'Wareneingang'
      });

      setMessage({ type: 'success', text: `✓ ${stockInput} ${selectedProduct.unit} hinzugefügt` });
      setShowStockModal(false);
      
      // Reload data
      setTimeout(() => {
        loadLowStock();
        setMessage(null);
      }, 2000);
    } catch (err) {
      setMessage({ type: 'error', text: 'Fehler: ' + err.message });
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 'var(--space-6)' }}>
        <div className="text-center py-8 text-[var(--text-secondary)]">Lade Daten...</div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">⚠️ Bestandswarnung</h1>
          <p className="page-subtitle">Produkte unter Mindestmenge</p>
        </div>
      </div>

      {message && (
        <div className={`mb-4 p-3 rounded-lg ${message.type === 'success' ? 'bg-[var(--success-bg)] text-[var(--success-text)] border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
          {message.text}
        </div>
      )}

      {lowStockProducts.length === 0 ? (
        <div className="dark-card" style={{ textAlign: 'center', padding: 'var(--space-8)', color: 'var(--text-secondary)' }}>
          <div style={{ fontSize: '3rem', marginBottom: 'var(--space-4)' }}>✅</div>
          <div style={{ fontSize: '1.125rem', marginBottom: 'var(--space-2)' }}>Alles im grünen Bereich</div>
          <div style={{ fontSize: '0.875rem' }}>Keine Produkte unter Mindestmenge</div>
        </div>
      ) : (
        <>
          {/* Desktop Table */}
          <div className="dark-card hidden lg:block">
            <div className="dark-table-container">
              <table className="dark-table">
                <thead>
                  <tr>
                    <th>Produkt</th>
                    <th>Kategorie</th>
                    <th style={{ width: '120px' }}>Bestand</th>
                    <th style={{ width: '120px' }}>Mindestmenge</th>
                    <th style={{ width: '100px' }}>Status</th>
                    <th style={{ width: '150px', textAlign: 'right' }}>Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {lowStockProducts.map(product => {
                    const percentage = (product.stock_quantity / product.min_stock) * 100;
                    const statusColor = percentage === 0 ? 'red' : percentage < 50 ? 'orange' : 'yellow';

                    return (
                      <tr key={product.product_id}>
                        <td>
                          <div style={{ fontWeight: 500, color: 'var(--text-primary)' }}>
                            {product.name}
                          </div>
                        </td>
                        <td>
                          <span style={{ color: 'var(--text-secondary)' }}>
                            {product.category_name}
                          </span>
                        </td>
                        <td>
                          <span style={{ fontWeight: 600, color: statusColor === 'red' ? '#EF4444' : statusColor === 'orange' ? '#F59E0B' : '#EAB308' }}>
                            {product.stock_quantity} {product.unit}
                          </span>
                        </td>
                        <td>
                          <span style={{ color: 'var(--text-secondary)' }}>
                            {product.min_stock} {product.unit}
                          </span>
                        </td>
                        <td>
                          {percentage === 0 ? (
                            <span className="badge badge-danger">Leer</span>
                          ) : percentage < 50 ? (
                            <span className="badge badge-warning">Kritisch</span>
                          ) : (
                            <span className="badge badge-warning">Niedrig</span>
                          )}
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
                            <button
                              onClick={() => openStockModal(product)}
                              className="btn-primary"
                              style={{ padding: 'var(--space-2) var(--space-3)', fontSize: '0.875rem' }}
                            >
                              + Wareneingang
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Cards */}
          <div className="lg:hidden space-y-3">
            {lowStockProducts.map(product => {
              const percentage = (product.stock_quantity / product.min_stock) * 100;
              const statusColor = percentage === 0 ? 'red' : percentage < 50 ? 'orange' : 'yellow';

              return (
                <div key={product.product_id} className="dark-card p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <h3 className="font-semibold text-base text-[var(--text-primary)] mb-1">
                        {product.name}
                      </h3>
                      <p className="text-sm text-[var(--text-secondary)]">{product.category_name}</p>
                    </div>
                    {percentage === 0 ? (
                      <span className="badge badge-danger text-xs">Leer</span>
                    ) : percentage < 50 ? (
                      <span className="badge badge-warning text-xs">Kritisch</span>
                    ) : (
                      <span className="badge badge-warning text-xs">Niedrig</span>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-3 mb-3 py-3 border-y border-[var(--border-primary)]">
                    <div>
                      <p className="text-xs text-[var(--text-secondary)] mb-1">Bestand</p>
                      <p className="text-lg font-bold" style={{ color: statusColor === 'red' ? '#EF4444' : statusColor === 'orange' ? '#F59E0B' : '#EAB308' }}>
                        {product.stock_quantity} {product.unit}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-[var(--text-secondary)] mb-1">Mindestmenge</p>
                      <p className="text-lg font-semibold">{product.min_stock} {product.unit}</p>
                    </div>
                  </div>

                  <button
                    onClick={() => openStockModal(product)}
                    className="btn-primary w-full text-sm py-3"
                  >
                    + Wareneingang buchen
                  </button>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Stock Adjustment Modal */}
      {showStockModal && selectedProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowStockModal(false)}>
          <div className="dark-card rounded-lg shadow-xl p-6 w-[500px]" onClick={e => e.stopPropagation()}>
            <h2 className="text-2xl font-bold mb-4">Wareneingang buchen</h2>

            <div className="mb-4 p-4 bg-[var(--bg-tertiary)] rounded-lg">
              <div className="text-sm text-[var(--text-secondary)]">Produkt:</div>
              <div className="text-xl font-bold text-[var(--text-primary)]">{selectedProduct.name}</div>
              <div className="text-sm text-[var(--text-secondary)] mt-2">
                Aktueller Bestand: <span className="font-semibold">{selectedProduct.stock_quantity} {selectedProduct.unit}</span>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Menge hinzufügen:</label>
              <input
                type="number"
                value={stockInput}
                onChange={e => setStockInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && stockInput) {
                    handleStockAdjustment();
                  }
                  if (e.key === 'Escape') {
                    setShowStockModal(false);
                  }
                }}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg text-2xl font-bold text-center focus:border-blue-500 focus:outline-none"
                placeholder="0"
                min="1"
                autoFocus
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Notiz (optional):</label>
              <input
                type="text"
                value={noteInput}
                onChange={e => setNoteInput(e.target.value)}
                className="dark-input"
                placeholder="z.B. Lieferung METRO"
              />
            </div>

            {stockInput && parseInt(stockInput) > 0 && (
              <div className="mb-4 p-4 bg-green-50 rounded-lg border-2 border-green-200">
                <div className="text-sm text-gray-600">Neuer Bestand:</div>
                <div className="text-3xl font-bold text-green-600">
                  {selectedProduct.stock_quantity + parseInt(stockInput)} {selectedProduct.unit}
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => setShowStockModal(false)}
                className="btn-secondary flex-1"
              >
                Abbrechen (Esc)
              </button>
              <button
                onClick={handleStockAdjustment}
                disabled={!stockInput || parseInt(stockInput) <= 0 || processing}
                className="btn-primary flex-1 disabled:opacity-50"
              >
                {processing ? 'Wird gebucht...' : '✓ Buchen (Enter)'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Bestandswarnung;
